import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Chip, Divider, Dropdown, DropdownMenuItem, Stack, TextField, Typography} from 'views/components';
import {Translate} from 'views/module';

import CareplanTextbox from './CareplanTextbox';

const CareplanDraft = (props) => {
  const {actions, careplanDraft, onSectionChange} = props;
  const [caregiverRequirementChipInput, setCaregiverRequirementChipInput] = React.useState('');
  const {careRoutine, caregiverRequirements, generalInformation, medicalHistory} = careplanDraft;

  const handleTextFieldChange = (sectionKey, fieldKey, value) => {
    onSectionChange(sectionKey, {
      ...careplanDraft[sectionKey],
      [fieldKey]: value,
    });
  };

  const handleMarkdownFieldChange = (sectionKey, fieldKey, value) => {
    onSectionChange(sectionKey, {
      ...careplanDraft[sectionKey],
      [fieldKey]: {
        ...careplanDraft[sectionKey][fieldKey],
        blob: value,
      },
    });
  };

  const handleCaregiverRequirementChipAdd = () => {
    const chipInputValue = caregiverRequirementChipInput.trim();

    if (chipInputValue === '') {
      return;
    }

    if (caregiverRequirements.caregiverRequirementChips.includes(chipInputValue)) {
      setCaregiverRequirementChipInput('');
      return;
    }

    onSectionChange('caregiverRequirements', {
      ...caregiverRequirements,
      caregiverRequirementChips: [...caregiverRequirements.caregiverRequirementChips, chipInputValue],
    });

    setCaregiverRequirementChipInput('');
  };

  const handleCaregiverRequirementChipDelete = (chipIndex) => {
    onSectionChange('caregiverRequirements', {
      ...caregiverRequirements,
      caregiverRequirementChips: caregiverRequirements.caregiverRequirementChips.filter((_chip, index) => index !== chipIndex),
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #CDD7FF',
        borderRadius: '16px',
        boxShadow: '0px 1px 2px rgba(130, 145, 255, 0.06)',
        overflow: 'visible',
      }}
    >
      <Stack
        sx={{
          px: '18px',
          py: '12px',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#FBFAF8',
            borderRadius: '8px',
            p: '12px',
          }}
        >
          <Stack
            spacing={0}
            divider={
              <Divider
                sx={{
                  borderColor: '#F1ECE5',
                }}
              />
            }
          >
            <Stack
              spacing={1.5}
              sx={{
                pt: '0px',
                pb: '18px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '150%',
                  color: '#1A1916',
                }}
              >
                <Translate expression='client.ClientDetail.Careplan.GeneralInformation.Title' />
              </Typography>
              <Stack spacing={2}>
                <CareplanTextbox
                  expression='client.ClientDetail.Careplan.GeneralInformation.ClientName'
                  onChange={(value) => {
                    handleTextFieldChange('generalInformation', 'clientName', value);
                  }}
                  value={generalInformation.clientName}
                />
                <CareplanTextbox
                  expression='client.ClientDetail.Careplan.GeneralInformation.Address'
                  onChange={(value) => {
                    handleTextFieldChange('generalInformation', 'address', value);
                  }}
                  value={generalInformation.address}
                />
                <CareplanTextbox
                  expression='client.ClientDetail.Careplan.GeneralInformation.Information'
                  onChange={(value) => {
                    handleMarkdownFieldChange('generalInformation', 'clientInfo', value);
                  }}
                  value={generalInformation.clientInfo.blob}
                />
                <CareplanTextbox
                  expression='client.ClientDetail.Careplan.GeneralInformation.Notes'
                  onChange={(value) => {
                    handleMarkdownFieldChange('generalInformation', 'notes', value);
                  }}
                  value={generalInformation.notes.blob}
                />
              </Stack>
            </Stack>
            <Stack
              spacing={1.5}
              sx={{
                pt: '18px',
                pb: '18px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '150%',
                  color: '#1A1916',
                }}
              >
                <Translate expression='client.ClientDetail.Careplan.MedicalHistory.Title' />
              </Typography>
              <Stack spacing={2}>
                <CareplanTextbox
                  expression='client.ClientDetail.Careplan.MedicalHistory.Conditions'
                  onChange={(value) => {
                    handleMarkdownFieldChange('medicalHistory', 'conditions', value);
                  }}
                  value={medicalHistory.conditions.blob}
                />
                <CareplanTextbox
                  expression='client.ClientDetail.Careplan.MedicalHistory.Medications'
                  onChange={(value) => {
                    handleMarkdownFieldChange('medicalHistory', 'medications', value);
                  }}
                  value={medicalHistory.medications.blob}
                />
              </Stack>
            </Stack>
            <Stack
              spacing={1.5}
              sx={{
                pt: '18px',
                pb: '18px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '150%',
                  color: '#1A1916',
                }}
              >
                <Translate expression='client.ClientDetail.Careplan.CareRoutine.Title' />
              </Typography>
              <Stack spacing={2}>
                <CareplanTextbox
                  expression='client.ClientDetail.Careplan.CareRoutine.ClientPreferences'
                  onChange={(value) => {
                    handleMarkdownFieldChange('careRoutine', 'clientPreferences', value);
                  }}
                  value={careRoutine.clientPreferences.blob}
                />
                <CareplanTextbox
                  expression='client.ClientDetail.Careplan.CareRoutine.Tasks'
                  onChange={(value) => {
                    handleMarkdownFieldChange('careRoutine', 'dailyTasks', value);
                  }}
                  value={careRoutine.dailyTasks.blob}
                />
              </Stack>
            </Stack>
            <Stack
              spacing={1.5}
              sx={{
                pt: '18px',
                pb: '18px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '150%',
                  color: '#1A1916',
                }}
              >
                <Translate expression='client.ClientDetail.Careplan.CaregiverRequirements.Title' />
              </Typography>
              <Stack spacing={2}>
                <CareplanTextbox
                  expression='client.ClientDetail.Careplan.CaregiverRequirements.CareplanSummary'
                  onChange={(value) => {
                    handleTextFieldChange('caregiverRequirements', 'careplanSummary', value);
                  }}
                  value={caregiverRequirements.careplanSummary}
                />
                <CareplanTextbox
                  expression='client.ClientDetail.Careplan.CaregiverRequirements.ScheduleRequirement'
                  onChange={(value) => {
                    handleTextFieldChange('caregiverRequirements', 'scheduleRequirement', value);
                  }}
                  value={caregiverRequirements.scheduleRequirement}
                />
                <Stack spacing={0.75}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      lineHeight: '150%',
                      color: '#6F6A62',
                    }}
                  >
                    <Translate expression='client.ClientDetail.Careplan.CaregiverRequirements.MatchDifficulty' />
                  </Typography>
                  <Box
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: '#FFFFFF',
                      },
                      '& .MuiSelect-select': {
                        paddingTop: '10px',
                        paddingBottom: '14px',
                      },
                      '& .MuiInputLabel-root': {
                        display: 'none',
                      },
                      '& .MuiOutlinedInput-notchedOutline legend': {
                        display: 'none',
                      },
                    }}
                  >
                    <Dropdown
                      onChange={(value) => {
                        handleTextFieldChange('caregiverRequirements', 'matchDifficulty', value);
                      }}
                      value={caregiverRequirements.matchDifficulty}
                    >
                      <DropdownMenuItem value='EASY'>
                        <Translate expression='client.ClientDetail.Careplan.CaregiverRequirements.MatchDifficultyOptions.EASY' />
                      </DropdownMenuItem>
                      <DropdownMenuItem value='MEDIUM'>
                        <Translate expression='client.ClientDetail.Careplan.CaregiverRequirements.MatchDifficultyOptions.MEDIUM' />
                      </DropdownMenuItem>
                      <DropdownMenuItem value='HARD'>
                        <Translate expression='client.ClientDetail.Careplan.CaregiverRequirements.MatchDifficultyOptions.HARD' />
                      </DropdownMenuItem>
                    </Dropdown>
                  </Box>
                </Stack>
                <Stack spacing={0.75}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      lineHeight: '150%',
                      color: '#6F6A62',
                    }}
                  >
                    <Translate expression='client.ClientDetail.Careplan.CaregiverRequirements.RequirementChips' />
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E8E4DE',
                      borderRadius: '8px',
                      p: '12px',
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems='flex-start'>
                        <TextField
                          fullWidth
                          label={<Translate expression='client.ClientDetail.Careplan.CaregiverRequirements.RequirementChipPlaceholder' />}
                          onChange={(value) => {
                            setCaregiverRequirementChipInput(value);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleCaregiverRequirementChipAdd();
                            }
                          }}
                          value={caregiverRequirementChipInput}
                        />
                        <Button
                          disableElevation
                          onClick={handleCaregiverRequirementChipAdd}
                          sx={{
                            'height': '56px',
                            'px': '10px',
                            'borderRadius': '7px',
                            'fontSize': '11px',
                            'fontWeight': 400,
                            'lineHeight': '100%',
                            'textTransform': 'none',
                            'whiteSpace': 'nowrap',
                            'color': '#FFFFFF',
                            'backgroundColor': '#E4875B',
                            '&:hover': {
                              backgroundColor: '#DB7D51',
                            },
                          }}
                          variant='contained'
                        >
                          <Translate expression='generic.Add' />
                        </Button>
                      </Stack>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        {
                          caregiverRequirements.caregiverRequirementChips.map((chip, chipIndex) => (
                            <Chip
                              key={`${chip}-${chipIndex}`}
                              label={chip}
                              onDelete={() => {
                                handleCaregiverRequirementChipDelete(chipIndex);
                              }}
                            />
                          ))
                        }
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Box>
        <Stack
          direction='row'
          sx={{
            gap: '10px',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            mt: '8px',
          }}
        >
          {
            actions.map((action) => (
              <Button
                key={action.key}
                variant={
                  action.variant === 'secondary' ? 'outlined' :
                  action.variant === 'tertiary' ? 'text' :
                  undefined
                }
                disableElevation={action.variant === 'primary'}
                disabled={action.disabled}
                onClick={action.onClick}
                sx={{
                  ...(action.variant === 'primary' && {
                    'px': '10px',
                    'py': '8px',
                    'borderRadius': '7px',
                    'fontSize': '11px',
                    'fontWeight': 400,
                    'lineHeight': '100%',
                    'textTransform': 'none',
                    'color': '#FFFFFF',
                    'backgroundColor': '#E4875B',
                    '&:hover': {
                      backgroundColor: '#DB7D51',
                    },
                  }),
                  ...(action.variant === 'secondary' && {
                    'height': '42px',
                    'textTransform': 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }),
                  ...(action.variant === 'tertiary' && {
                    'px': '4px',
                    'py': '10px',
                    'borderRadius': '7px',
                    'fontSize': '11px',
                    'fontWeight': 400,
                    'lineHeight': '100%',
                    'textTransform': 'none',
                    'color': '#6F6A62',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#1A1916',
                    },
                  }),
                  ...action.sx,
                }}
              >
                <Translate expression={action.expression} />
              </Button>
            ))
          }
        </Stack>
      </Stack>
    </Box>
  );
};

CareplanDraft.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    expression: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    sx: PropTypes.object,
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary']).isRequired,
  })).isRequired,
  careplanDraft: PropTypes.shape({
    careRoutine: PropTypes.shape({
      clientPreferences: PropTypes.shape({
        blob: PropTypes.string.isRequired,
        editorType: PropTypes.string.isRequired,
      }).isRequired,
      dailyTasks: PropTypes.shape({
        blob: PropTypes.string.isRequired,
        editorType: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    caregiverRequirements: PropTypes.shape({
      careplanSummary: PropTypes.string.isRequired,
      caregiverRequirementChips: PropTypes.arrayOf(PropTypes.string).isRequired,
      matchDifficulty: PropTypes.string.isRequired,
      scheduleRequirement: PropTypes.string.isRequired,
    }).isRequired,
    generalInformation: PropTypes.shape({
      address: PropTypes.string.isRequired,
      clientInfo: PropTypes.shape({
        blob: PropTypes.string.isRequired,
        editorType: PropTypes.string.isRequired,
      }).isRequired,
      clientName: PropTypes.string.isRequired,
      notes: PropTypes.shape({
        blob: PropTypes.string.isRequired,
        editorType: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    medicalHistory: PropTypes.shape({
      conditions: PropTypes.shape({
        blob: PropTypes.string.isRequired,
        editorType: PropTypes.string.isRequired,
      }).isRequired,
      medications: PropTypes.shape({
        blob: PropTypes.string.isRequired,
        editorType: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  onSectionChange: PropTypes.func.isRequired,
};

export default React.memo(CareplanDraft);
